import { ServerMessagePayload } from '@common/api';

import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/**
 * Applies per-frame transform modifiers from spritesheet user_data.
 *
 * Reads the current frame's FrameTransformModifier from SpritesheetService and
 * interpolates a triangle wave within the frame duration:
 * - Position/rotation: 0 → target → 0
 * - Scale: 1.0 → target → 1.0
 *
 * Writes results to AnimationTransformOffset component for RenderSystem to apply.
 *
 * Example user_data: "y: -5; scale: 2" creates a bounce effect where the sprite
 * moves up 5 pixels and scales to 2x at the midpoint of the frame.
 * Use "scaleX: -1" to flip horizontally (spinning coin effect).
 */
export class AnimationTransformSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['Animation', 'SpritesheetSprite'];

  updateEntity({
    world,
    entityId,
  }: FilteredClientComponentSystemUpdateEntityArgs): ServerMessagePayload[] {
    const animation = world.getComponent(entityId, 'Animation');
    const spritesheetSprite = world.getComponent(entityId, 'SpritesheetSprite');
    const spritesheetService = this.clientServices.spritesheet;

    if (!animation || !spritesheetSprite || !animation.isPlaying) {
      // Remove offset when not playing
      const existing = world.getComponent(entityId, 'AnimationTransformOffset');
      if (existing) {
        world.removeComponent(entityId, 'AnimationTransformOffset');
      }
      return [];
    }

    // Get animation metadata to calculate current sheet frame
    const animationData = spritesheetService.getAnimation(
      spritesheetSprite.spritesheetId,
      animation.name,
    );
    if (!animationData) {
      return [];
    }

    // Calculate current sheet frame from animation frame
    const currentSheetFrame = animationData.fromFrame + animation.animationFrame - 1;

    // Get transform modifier for this frame
    const modifier = spritesheetService.getFrameTransformModifier(
      spritesheetSprite.spritesheetId,
      currentSheetFrame,
    );

    if (!modifier) {
      // No modifier for this frame - remove offset if present
      const existing = world.getComponent(entityId, 'AnimationTransformOffset');
      if (existing) {
        world.removeComponent(entityId, 'AnimationTransformOffset');
      }
      return [];
    }

    // Calculate interpolation progress (0 to 1) within the frame
    const frameDuration = animation.currentFrameDurationMs ?? 1;
    const progress = Math.min(1, animation.timer / frameDuration);

    // Triangle wave: 0 → 1 → 0 over the frame
    // At progress 0: wave = 0
    // At progress 0.5: wave = 1
    // At progress 1: wave = 0
    const wave = 1 - Math.abs(2 * progress - 1);

    // Calculate interpolated values
    // For position/rotation: 0 → target → 0
    const x = (modifier.x ?? 0) * wave;
    const y = (modifier.y ?? 0) * wave;
    const rotation = (modifier.rotation ?? 0) * wave;

    // For scale: lerp from 1.0 → target → 1.0
    // If scaleX/scaleY not specified, use scale (default 1.0 = no change)
    const targetScaleX = modifier.scaleX ?? modifier.scale ?? 1;
    const targetScaleY = modifier.scaleY ?? modifier.scale ?? 1;
    // lerp(1.0, target, wave) = 1 + (target - 1) * wave
    const scaleX = 1 + (targetScaleX - 1) * wave;
    const scaleY = 1 + (targetScaleY - 1) * wave;

    // Get or create AnimationTransformOffset component
    let offset = world.getComponent(entityId, 'AnimationTransformOffset');
    if (!offset) {
      offset = world.createComponent('AnimationTransformOffset', {
        x,
        y,
        scaleX,
        scaleY,
        rotation,
      });
      world.addComponent(entityId, offset);
    } else {
      world.updateComponent(entityId, offset, {
        x,
        y,
        scaleX,
        scaleY,
        rotation,
      });
    }

    return [];
  }
}
